import React from 'react';
import CardIndicador from './CardIndicador';
import TabelaMediaEquipes from './TabelaMediaEquipes';
import GraficoBarras from './GraficoBarras';

/**
 * Verifica se a competência está encerrada.
 * Considera encerrada quando a data_fim é anterior ou igual a hoje.
 */
function competenciaEncerrada(dataFim) {
  if (!dataFim) return false;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const fim = new Date(`${dataFim}T23:59:59`);
  return fim <= hoje;
}

export default function AbaGeral({ geral, carregando }) {
  if (carregando) {
    return <div style={{ color: 'var(--muted)', padding: '40px', textAlign: 'center' }}>Carregando...</div>;
  }

  if (!geral) return null;

  const { cards, competencia: comp, previsoes, us_diario, media_equipes, atualizado_em } = geral;
  const encerrada = competenciaEncerrada(comp?.fim);

  return (
    <div>
      {/* LINHA 1: Info principal */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '24px',
      }}>
        {/* Esquerda: US Total + AL/RA + Previsão (se aberta) */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderLeft: '4px solid var(--amber)',
          borderRadius: 'var(--radius-md)',
          padding: '24px',
        }}>
          <div style={{
            fontSize: 'var(--font-sm)',
            color: 'var(--muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '8px',
          }}>
            US Total
          </div>
          <div style={{
            fontSize: 'var(--font-3xl)',
            fontWeight: 700,
            color: 'var(--amber)',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.1,
          }}>
            {cards?.total?.valor != null
              ? cards.total.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
              : '\u2014'}
          </div>
          <div style={{
            display: 'flex',
            gap: '16px',
            marginTop: '12px',
            fontSize: 'var(--font-sm)',
            color: 'var(--muted)',
          }}>
            {cards?.total?.al != null && <span>AL: {cards.total.al.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>}
            {cards?.total?.ra != null && <span>RA: {cards.total.ra.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>}
          </div>
          {!encerrada && previsoes?.total_previsto != null && (
            <div style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: '1px solid var(--border)',
            }}>
              <div style={{ fontSize: 'var(--font-xs)', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Previsao Fechamento
              </div>
              <div style={{ fontSize: 'var(--font-base)', color: 'var(--amber)', fontWeight: 600 }}>
                ~{previsoes.total_previsto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} US
              </div>
            </div>
          )}
        </div>

        {/* Direita: Competência + Apropriação + Período */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '16px',
        }}>
          {/* Competência e Período na mesma linha */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 'var(--font-xs)', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Competencia
              </div>
              <div style={{ fontSize: 'var(--font-lg)', color: 'var(--text)', fontWeight: 600 }}>
                {comp?.nome || '\u2014'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 'var(--font-xs)', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Periodo
              </div>
              <div style={{ fontSize: 'var(--font-base)', color: 'var(--text)' }}>
                {comp?.inicio ? `${comp.inicio.substring(8,10)}/${comp.inicio.substring(5,7)}` : '\u2014'} a {comp?.fim ? `${comp.fim.substring(8,10)}/${comp.fim.substring(5,7)}` : '\u2014'}
              </div>
            </div>
          </div>
          {/* Apropriação */}
          <div>
            <div style={{ fontSize: 'var(--font-xs)', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Apropriacao
            </div>
            <div style={{ fontSize: 'var(--font-lg)', color: 'var(--text)', fontWeight: 600 }}>
              {comp?.id || '\u2014'}
            </div>
          </div>
          {/* Atualizado em */}
          <div>
            <div style={{ fontSize: 'var(--font-xs)', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Atualizado em
            </div>
            <div style={{ fontSize: 'var(--font-base)', color: 'var(--text)' }}>
              {atualizado_em || '\u2014'}
            </div>
          </div>
        </div>
      </div>

      {/* LINHA 2: Cards (Serviços, Sobreaviso, Ajustes) + Tabela Média */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '24px',
        alignItems: 'start',
      }}>
        {/* Esquerda: 3 Cards empilhados */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <CardIndicador
            label="Servicos"
            valor={cards?.servicos?.valor}
            al={cards?.servicos?.al}
            ra={cards?.servicos?.ra}
          />
          <CardIndicador
            label="Sobreaviso"
            valor={cards?.sobreaviso?.valor}
            al={cards?.sobreaviso?.al}
            ra={cards?.sobreaviso?.ra}
            tipo="sobreaviso"
          />
          <CardIndicador
            label="Ajustes"
            valor={cards?.ajuste?.valor}
            al={cards?.ajuste?.al}
            ra={cards?.ajuste?.ra}
            tipo="ajuste"
          />
        </div>

        {/* Direita: Tabela Média por Tipo */}
        <TabelaMediaEquipes dados={media_equipes || []} />
      </div>

      {/* LINHA 3: Gráfico US Diário — largura total */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '20px',
        marginBottom: '32px',
      }}>
        <div style={{
          fontSize: 'var(--font-sm)',
          fontWeight: 600,
          color: 'var(--amber)',
          marginBottom: '16px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          US Diario
        </div>
        <GraficoBarras
          data={us_diario || []}
          labelKey="data"
          valueKey="us_total"
          qtdKey="qtd_equipes"
          formatter={(v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          height={260}
          emptyMessage="Nenhum dado diario"
        />
      </div>
    </div>
  );
}
